import { ApolloError, BaseMutationOptions } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { generateDataDogContext } from './dataDog'

/**
 * How long a cart add or remove may stay in flight before it is aborted and
 * treated as failed. Armed only with `globals.counter.disableWhileSyncing` on:
 * a request that never settles would otherwise leave the sku's Counters
 * disabled for good, since the Apollo client (`config/apolloClient.ts`) sets no
 * request timeout of its own.
 */
export const CART_MUTATION_TIMEOUT_MS = 15000

export type CartMutationOperation = 'add' | 'remove' | 'delete'

/*
 * Signals the timer aborted, as opposed to a newer tap superseding the
 * request. Tracked here rather than read from `signal.reason`, which jsdom and
 * older webviews do not carry, so the two aborts cannot be told apart there.
 */
const timedOutSignals = new WeakSet<AbortSignal>()

/**
 * Aborts `controller` once `CART_MUTATION_TIMEOUT_MS` passes, unless it was
 * already aborted. Returns the function that disarms the timer; call it when
 * the request settles.
 */
export const armCartMutationTimeout = (controller: AbortController) => {
  const timer = setTimeout(() => {
    if (controller.signal.aborted) return
    timedOutSignals.add(controller.signal)
    controller.abort(
      new DOMException('cart mutation timed out', 'TimeoutError'),
    )
  }, CART_MUTATION_TIMEOUT_MS)
  return () => clearTimeout(timer)
}

export const hasCartMutationTimedOut = (signal?: AbortSignal | null) =>
  !!signal && timedOutSignals.has(signal)

/** The signal a cart mutation was sent with, from Apollo's `onError` options. */
export const signalOfMutation = (options?: BaseMutationOptions) =>
  options?.context?.fetchOptions?.signal as AbortSignal | undefined

export const reportCartMutationTimeout = ({
  error,
  sku,
  operation,
  caller,
}: {
  error: ApolloError
  sku?: string
  operation: CartMutationOperation
  caller: string
}) => {
  const context = generateDataDogContext({
    title: 'cart mutation timed out',
    extraInfo: {
      sku,
      operation,
      timeoutMs: CART_MUTATION_TIMEOUT_MS,
      function: caller,
    },
  })
  datadogRum.startView(context.viewName)
  datadogRum.addError(error, context)
}
